/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
/* eslint-disable no-unused-vars */
import {useThemeMode} from '../theme/useThemeContext';
import {IconProps} from '../types/types';
import {resolveIconColor} from './useIconColor';
import useIconSize from './useIconSize';

interface IconDLS {
  iconSize: number;
  iconColor: string;
  fill?: string | undefined;
  isDark: boolean;
};

/**
 * Defines the useIconDLS hook which returns the icon color and size based
 * on the provided default values, if any, and whether dark mode is active.
 * The hook subscribes once to the theme mode, so the icon re-renders with
 * the color of the active theme mode when the mode changes. An icon which
 * needs the mode, for example to swap a hard-coded color, uses the returned
 * `isDark` rather than a theme hook of its own.
 *
 * ```typescript
 *   import {useIconDLS} from '@ion/dls/src/icons/useIconDLS';
 *   import {dlsDarkColor} from '@ion/dls/src/theme/theme';
 *   ...
 *
 *   export const Icon = (props: IconProps) => {
 *     const {iconSize, isDark} = useIconDLS(props);
 *     const stroke             = dlsDarkColor(isDark, 'grey3', '#828282');
 *     return (
 *       <svg width={iconSize} height={iconSize} viewBox='0 0 24 24' fill='none'>
 *         <path d='M10.484 18a1.313 1.313 0 100-2.625 1.313 1.313 0 000 2.625z'
 *          stroke={stroke}
 *          />
 *       </svg>
 *     );
 *   };
 *
 * ```
 */
export const useIconDLS = ({size, color, fill}: IconProps): IconDLS => {
  const iconSize  = useIconSize(size);
  const {isDark}  = useThemeMode();
  const iconColor = resolveIconColor(color);
  return {iconSize, iconColor, fill, isDark};
};
export default useIconDLS;
